/**
 * Group settings (Phase B1) — group_admin only. Temporary scaffolding,
 * pre-nav-refactor, same as the other /groups/* entry points.
 */
import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ArrowLeft } from 'lucide-react'
import { REGIONS, SYSTEM_BADGE_SLUGS } from '@olimhub/shared'
import type { Region, GeocodedPoint } from '@olimhub/shared'
import { groupsApi } from '@/api'
import { useAuthStore } from '@/store/auth.store'
import { useToast } from '@/components/common/Toast'
import { useCategoryOptions, resolveActiveLanguage } from '@/hooks'
import { ImageUpload } from '@/components/post/ImageUpload'
import { AddressAutocompleteInput, SectionCard } from '@/components/common'

export function GroupSettingsPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const { account: authAccount } = useAuthStore()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const uiLanguage = resolveActiveLanguage(i18n.language)
  const { data: group, isLoading: groupLoading } = useQuery({
    queryKey: ['group', slug, authAccount?.id ?? 'anon', uiLanguage],
    queryFn: () => groupsApi.getBySlug(slug!, uiLanguage),
    enabled: !!slug,
  })
  const { data: activeMembers, isLoading: membersLoading } = useQuery({
    queryKey: ['group-members', slug, 'active'],
    queryFn: () => groupsApi.getMembers(slug!, 'active'),
    enabled: !!slug,
  })

  const viewerRoleBadge = activeMembers
    ?.find((m) => m.account.id === authAccount?.id)
    ?.account.badges.find((ab) => ab.badge.scope === 'group' && ab.groupId === group?.id)
  const isGroupAdmin = viewerRoleBadge?.badge.slug === SYSTEM_BADGE_SLUGS.GROUP_ADMIN

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null)
  const [headerUrl, setHeaderUrl] = useState<string | null>(null)
  const [isOpen, setIsOpen] = useState(true)
  const [region, setRegion] = useState<Region | ''>('')
  const [locationText, setLocationText] = useState('')
  // Set when the user picks a Google-resolved suggestion — lets submit skip
  // enqueueing the async geocode job. Cleared automatically if locationText
  // is edited after picking (AddressAutocompleteInput's stale-pick guard),
  // and whenever the group's data reloads (below), since a prior pick can't
  // still apply to a freshly-fetched locationText.
  const [resolvedPoint, setResolvedPoint] = useState<GeocodedPoint | null>(null)
  const [site, setSite] = useState('')
  const [whatsapp, setWhatsapp] = useState('')
  const [instagram, setInstagram] = useState('')
  const [categoryId, setCategoryId] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const { options: categoryOptions, language: categoryLang } = useCategoryOptions('group')

  useEffect(() => {
    if (!group) return
    setName(group.name)
    setDescription(group.description ?? '')
    setAvatarUrl(group.avatarUrl)
    setHeaderUrl(group.headerUrl)
    setIsOpen(group.isOpen)
    setRegion(group.region ?? '')
    setLocationText(group.locationText ?? '')
    setResolvedPoint(null)
    setSite(group.links?.['site'] ?? '')
    setWhatsapp(group.links?.['whatsapp'] ?? '')
    setInstagram(group.links?.['instagram'] ?? '')
    setCategoryId(group.category?.id ?? null)
  }, [group])

  async function handleSubmit() {
    if (!slug || !name.trim() || submitting) return
    setSubmitting(true)
    const links: Record<string, string> = {}
    if (site.trim()) links['site'] = site.trim()
    if (whatsapp.trim()) links['whatsapp'] = whatsapp.trim()
    if (instagram.trim()) links['instagram'] = instagram.trim()
    try {
      await groupsApi.update(slug, {
        name: name.trim(),
        description: description.trim() || null,
        avatarUrl,
        headerUrl,
        isOpen,
        region: region || null,
        locationText: locationText.trim() || null,
        ...(resolvedPoint && { latitude: resolvedPoint.latitude, longitude: resolvedPoint.longitude }),
        links: Object.keys(links).length > 0 ? links : null,
        categoryId: categoryId || null,
      }, uiLanguage)
      await queryClient.invalidateQueries({ queryKey: ['group', slug] })
      toast.success(t('groups.settings_saved'))
      navigate(`/groups/${slug}`)
    } catch {
      toast.error(t('groups.settings_save_failed'))
    } finally {
      setSubmitting(false)
    }
  }

  if (groupLoading || membersLoading) {
    return <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-faint)' }}>{t('common.loading')}</div>
  }
  if (!group) {
    return <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-faint)' }}>{t('groups.not_found')}</div>
  }
  if (!isGroupAdmin) {
    return <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-faint)' }}>{t('groups.error_forbidden')}</div>
  }

  return (
    <div style={{ maxWidth: 480, margin: '0 auto' }}>
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: 'var(--surface)',
          borderBottom: '1px solid var(--border)',
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <button
          type="button"
          aria-label="Back"
          onClick={() => navigate(`/groups/${slug}`)}
          style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', padding: 4 }}
        >
          <ArrowLeft size={20} />
        </button>
        <span style={{ fontFamily: 'var(--font-sans)', fontWeight: 700, fontSize: 'var(--fs-body)', color: 'var(--text-strong)' }}>
          {t('groups.settings_title')}
        </span>
      </div>

      <div style={{ padding: 16 }}>

      <SectionCard title={t('groups.section_images')}>
        <label style={labelStyle}>{t('groups.avatar_label')}</label>
        <ImageUpload type="avatar" currentUrl={avatarUrl} onUploaded={setAvatarUrl} onRemoved={() => setAvatarUrl(null)} />

        <label style={labelStyle}>{t('groups.header_label')}</label>
        <ImageUpload type="post" currentUrl={headerUrl} onUploaded={setHeaderUrl} onRemoved={() => setHeaderUrl(null)} />
      </SectionCard>

      <SectionCard title={t('groups.section_basic_info')}>
        <label style={labelStyle}>{t('groups.name_label')}</label>
        <input value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} maxLength={64} />

        <label style={labelStyle}>{t('groups.description_label')}</label>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} maxLength={500} />

        <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={isOpen} onChange={(e) => setIsOpen(e.target.checked)} style={{ width: 16, height: 16, accentColor: 'var(--brand)' }} />
          {t('groups.is_open_label')}
        </label>

        <label style={labelStyle}>{t('post.category')} ({t('events.optional')})</label>
        <select value={categoryId ?? ''} onChange={(e) => setCategoryId(e.target.value || null)} style={inputStyle}>
          <option value="">{t('post.category_none')}</option>
          {categoryOptions.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {cat.translations[categoryLang] ?? cat.translations.en}
            </option>
          ))}
        </select>
      </SectionCard>

      <SectionCard title={t('groups.section_location')}>
        <label style={labelStyle}>{t('groups.region_label')}</label>
        <select value={region} onChange={(e) => setRegion(e.target.value as Region | '')} style={inputStyle}>
          <option value="">{t('groups.region_none')}</option>
          {REGIONS.map((r) => (
            <option key={r} value={r}>{t(`groups.region.${r}`)}</option>
          ))}
        </select>

        <label style={labelStyle}>{t('groups.location_label')}</label>
        <AddressAutocompleteInput
          value={locationText}
          onChange={setLocationText}
          onSelect={(_, resolved) => setResolvedPoint(resolved ?? null)}
          placeholder={t('groups.location_placeholder')}
          style={inputStyle}
          maxLength={200}
        />
        <p style={{ margin: '4px 0 0', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', color: 'var(--text-faint)' }}>
          {t('groups.location_hint')}
        </p>
      </SectionCard>

      <SectionCard title={t('groups.section_links')}>
        <label style={labelStyle}>{t('groups.link_site')}</label>
        <input value={site} onChange={(e) => setSite(e.target.value)} style={inputStyle} placeholder="https://…" />

        <label style={labelStyle}>{t('groups.link_whatsapp')}</label>
        <input value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} style={inputStyle} placeholder="https://chat.whatsapp.com/…" />

        <label style={labelStyle}>{t('groups.link_instagram')}</label>
        <input value={instagram} onChange={(e) => setInstagram(e.target.value)} style={inputStyle} placeholder="https://instagram.com/…" />
      </SectionCard>

      <button
        type="button"
        disabled={!name.trim() || submitting}
        onClick={() => void handleSubmit()}
        style={{
          marginTop: 4,
          width: '100%',
          padding: '11px 0',
          borderRadius: 'var(--radius-md)',
          border: 'none',
          background: 'var(--brand)',
          color: 'var(--on-brand)',
          fontFamily: 'var(--font-sans)',
          fontWeight: 700,
          fontSize: 'var(--fs-body)',
          cursor: name.trim() && !submitting ? 'pointer' : 'not-allowed',
          opacity: name.trim() ? 1 : 0.55,
        }}
      >
        {t('groups.save_settings')}
      </button>
      </div>
    </div>
  )
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginTop: 14,
  marginBottom: 6,
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--fs-xs)',
  fontWeight: 600,
  color: 'var(--text-muted)',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  borderRadius: 'var(--radius-md)',
  border: '1.5px solid var(--border)',
  background: 'var(--bg-sunk)',
  color: 'var(--text-strong)',
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--fs-body)',
  boxSizing: 'border-box',
  outline: 'none',
}
